import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';

const WalletInfoCard = () => {
  const { userData } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWalletData = async () => {
      if (!userData) {
        setLoading(false);
        return;
      }

      // Ellenőrizzük, hogy a felhasználónak van-e wallet kapcsolata
      if (!userData.walletConnected || !userData.walletId) {
        setLoading(false);
        setWalletData(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('🔍 Fetching wallet data for ID:', userData.walletId);
        
        const walletRef = doc(db, 'walletUsers', userData.walletId);
        const walletDoc = await getDoc(walletRef);
        
        if (walletDoc.exists()) {
          const data = walletDoc.data();
          setWalletData({
            id: walletDoc.id,
            ...data
          });
          console.log('✅ Wallet data fetched:', data);
        } else {
          console.log('❌ No wallet found with ID:', userData.walletId);
          setWalletData(null);
          setError('A wallet adatok nem találhatók');
        }
      } catch (error) {
        console.error('❌ Error fetching wallet data:', error);
        setError('Hiba történt a wallet adatok lekérése során');
        setWalletData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();
  }, [userData]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Ha hiba történt
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Wallet Kapcsolat</h3>
          <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
            Hiba
          </span>
        </div>
        
        <p className="text-gray-600 mb-4">
          {error}
        </p>
        
        <Link 
          to="/profile/wallet" 
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700"
        >
          <span>Wallet kezelése</span>
          <Icons.ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    );
  }

  // Ha nincs wallet kapcsolat
  if (!userData?.walletConnected || !walletData) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Wallet Kapcsolat</h3>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
            Nincs kapcsolva
          </span>
        </div>
        
        <p className="text-gray-600 mb-4">
          Kapcsold össze a wallet fiókodat az Előny Klub fiókoddal, hogy hozzáférj a speciális kedvezményekhez és szolgáltatásokhoz.
        </p>
        
        <Link 
          to="/profile/wallet" 
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700"
        >
          <span>Wallet összekapcsolása</span>
          <Icons.ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    );
  }

  // Ha van wallet kapcsolat
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Wallet Kapcsolat</h3>
        <span className="px-2 py-1 bg-green-100 text-green-600 text-xs font-medium rounded-full">
          Összekapcsolva
        </span>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500 mb-1">Wallet cím</p>
          <p className="font-mono text-sm">
            {walletData.walletAddress ? (
              <>
                {walletData.walletAddress.substring(0, 8)}...
                {walletData.walletAddress.substring(walletData.walletAddress.length - 6)}
              </>
            ) : (
              'N/A'
            )}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-gray-500 mb-1">Egyenleg</p>
          <p className="font-medium text-lg">
            {walletData.balance || 0} {walletData.currency || 'TOKEN'}
          </p>
        </div>
      </div>
      
      <div className="flex justify-between items-center">
        <Link 
          to="/profile/wallet" 
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700"
        >
          <span>Wallet kezelése</span>
          <Icons.ArrowRight className="ml-2 h-4 w-4" />
        </Link>
        
        <a 
          href="#" 
          className="inline-flex items-center text-gray-600 hover:text-gray-700"
          onClick={(e) => {
            e.preventDefault();
            window.open('https://wallet.elonyklub.hu', '_blank');
          }}
        >
          <span>Wallet alkalmazás megnyitása</span>
          <Icons.ExternalLink className="ml-2 h-4 w-4" />
        </a>
      </div>
    </div>
  );
};

export default WalletInfoCard; 